import type { CollaborativeMapSubmissionsResponse } from '@/service/engageFrontendGateway';
import type { CollabMapCluster, CollabMapPin } from '../CollaborativeMapContext';

// Round to 6 decimal places (~10cm precision) so floating-point noise from JSON
// parsing doesn't produce different ids for the "same" grid cell.
const COORD_PRECISION = 6;
const roundCoord = (coord: number): string => coord.toFixed(COORD_PRECISION);

export function toPins(result: CollaborativeMapSubmissionsResponse): CollabMapPin[] {
  if (result.mode !== 'individual' || !result.pins) return [];
  return result.pins.map((pin) => ({
    uuid: pin.submissionId,
    lat: pin.lat,
    lng: pin.lng,
    submissionId: pin.submissionId,
    residentId: pin.residentId ?? undefined,
    inputId: pin.inputId,
    completedAt: pin.completedAt,
  }));
}

export function toClusters(result: CollaborativeMapSubmissionsResponse): CollabMapCluster[] {
  if (result.mode !== 'cluster' || !result.clusters) return [];
  return result.clusters.map((cluster) => ({
    id: `cluster:${roundCoord(cluster.lat)}:${roundCoord(cluster.lng)}`,
    position: { lat: cluster.lat, lng: cluster.lng },
    count: cluster.count,
  }));
}

export function dedupeBy<T>(items: T[], keyOf: (item: T) => string): T[] {
  return Array.from(new Map(items.map((item) => [keyOf(item), item])).values());
}

// Same-cell clusters from disjoint resident queries should sum, not overwrite —
// e.g. a grid cell with 50 own + 100 others is a 150-pin cluster, not 100.
export function mergeClusters(clusters: CollabMapCluster[]): CollabMapCluster[] {
  const merged = new Map<string, CollabMapCluster>();
  clusters.forEach((cluster) => {
    const existing = merged.get(cluster.id);
    merged.set(cluster.id, existing ? { ...cluster, count: existing.count + cluster.count } : cluster);
  });
  return Array.from(merged.values());
}
